import { Component, Input, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DonationCardService } from '../../../services/donation-card.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { DropdownOption, ParentDonationModel, ParentDonationSearchModel } from '../../../models/DonationCardModels';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectListItem } from '../../../models/SelectListItem';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete'; // Import the correct type
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-donation-parent-child-tab',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    CardModule,
    CheckboxModule,
    DropdownModule,
    AutoCompleteModule,
    ButtonModule,
    ReactiveFormsModule,
    RadioButtonModule,
    ToastModule
  ],
  templateUrl: './donation-parent-child-tab.component.html',
  styleUrl: './donation-parent-child-tab.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [MessageService]
})
export class DonationParentChildTabComponent implements OnInit {
  @Input() donationId!: number;
  @Input() splitId!: number;
  @Input() campaignId!: number;
  @Input() isReadOnlyMode: boolean = false;
  parentInfo?: ParentDonationModel;
  isChildDonation: boolean = false;
  selectedDonationType: string = 'parent';
  filteredParentDonationNames: string[] = [];
  parentDonationNumberOptions: { label: string, value: number }[] = [];
  showPacLimitNotAffected: boolean = false;

  // Modal and Form Variables
  isEditModalVisible = false;
  isEditDataLoading = false;
  editForm!: FormGroup;
  parentDonationTypeOptions: { text: string; value: number }[] = [];

  private parentDonorSearchResults: ParentDonationSearchModel[] = []; // Store original results

  constructor(private service: DonationCardService, private fb: FormBuilder, private messageService: MessageService) { }

  private loadReadOnlyForm(): void {
    this.service.getParentDonation(this.donationId, this.splitId).subscribe((data: ParentDonationModel) => {
      this.parentInfo = data;
      this.isChildDonation = this.parentInfo.parentDonor ? true : false;
      if (this.parentInfo.transferTypes) {
        this.parentDonationTypeOptions = this.parentInfo.transferTypes.map(type => ({
          text: type.text,
          value: Number(type.value)
        }));
      }
      this.initializeForm();
    });
  }

  ngOnInit() {
    this.loadReadOnlyForm();
  }

  initializeForm() {
    if (!this.parentInfo) {
      console.error('Parent info is not loaded');
      return;
    }

    this.editForm = this.fb.group({
      parentDonationTypeId: [this.parentInfo?.parentDonationTypeId || null, Validators.required],
      parentDonationType: [this.parentInfo?.parentDonationType || ''],
      parentDonorName: [this.parentInfo?.parentDonor || ''],
      parentDonorId: [this.parentInfo?.parentDonorId || ''],
      parentDonationId: [this.parentInfo?.parentDonationId || null],
      selectedDonationType: [this.isChildDonation ? 'child' : 'parent', Validators.required],
      pacLimitNotAffected: [false]
    });

    this.editForm.get('parentDonationTypeId')?.valueChanges.subscribe((selectedValue) => {
      if (!selectedValue) return;

      const isConduit = selectedValue === 2;
      this.showPacLimitNotAffected = isConduit && this.selectedDonationType === 'parent';

      if (!this.showPacLimitNotAffected) {
        this.editForm.patchValue({ pacLimitNotAffected: false });
      }

      const selectedOption = this.parentDonationTypeOptions.find(option => option.value === selectedValue);
      if (selectedOption) {
        this.editForm.patchValue({
          parentDonationType: selectedOption.text
        });
      }
    });

    this.editForm.get('selectedDonationType')?.valueChanges.subscribe((type) => {
      this.onDonationTypeChange(type);
    });

    // Check if it's a child donation and parentInfo.parentDonorId exists
    if (this.isChildDonation && this.parentInfo?.parentDonorId) {
      this.fetchParentDonationNumbers(this.parentInfo?.parentDonorId);
    }

    this.applyConditionalValidation();
  }

  applyConditionalValidation() {
    if (this.isChildDonation) {
      this.editForm.get('parentDonorName')?.setValidators(Validators.required);
      this.editForm.get('parentDonationId')?.setValidators(Validators.required);
    } else {
      this.editForm.get('parentDonorName')?.clearValidators();
      this.editForm.get('parentDonationId')?.clearValidators();
    }

    this.editForm.get('parentDonorName')?.updateValueAndValidity();
    this.editForm.get('parentDonationId')?.updateValueAndValidity();
  }

  isInvalid(controlName: string): boolean {
    const control = this.editForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  onEditClick(): void {
    this.isEditModalVisible = true;
    this.isChildDonation = this.parentInfo?.parentDonor ? true : false;
    this.initializeForm();
    if (this.parentInfo?.parentDonationTypeId === 2 && !this.isChildDonation) {
      this.showPacLimitNotAffected = true;
      if (this.parentInfo?.paclimitNotAffected) {
        this.editForm.patchValue({ pacLimitNotAffected: true });
      } else {
        this.editForm.patchValue({ pacLimitNotAffected: false });
      }
    } else {
      this.showPacLimitNotAffected = false;
    }
  }

  onEditSubmit(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.editForm.valid) {
      const updatedData = this.editForm.value;
      const payload = {
        isMasterDonation: updatedData.selectedDonationType === 'parent',
        transferTypeId: updatedData.parentDonationTypeId,
        parentDonationId: updatedData.parentDonationId,
        isPacLimitNotAffected: updatedData.pacLimitNotAffected,
      };
      this.isEditDataLoading = true;
      this.service.saveParentDonation(this.donationId, this.splitId, payload).subscribe({
        next: () => {
          this.isEditDataLoading = false;
          this.isEditModalVisible = false;

          this.loadReadOnlyForm(); 

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Parent donation information updated successfully.'
          });
        },
        error: () => {
          this.isEditDataLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update parent donation information. Please try again.'
          });
        }
      });
    } else {
      this.editForm.markAllAsTouched();
    }
  }

  onEditCancel(): void {
    this.isEditModalVisible = false;
  }

  onDonationTypeChange(type: string): void {
    this.selectedDonationType = type;
    this.isChildDonation = type === 'child';

    const isConduit = this.editForm.get('parentDonationTypeId')?.value === 2;
    this.showPacLimitNotAffected = isConduit && this.selectedDonationType === 'parent';

    if (this.isChildDonation) {
      this.editForm.patchValue({
        parentDonorName: '',
        parentDonorId: null,
        parentDonationId: null,
        pacLimitNotAffected: false
      });

      this.editForm.get('parentDonorName')?.setValidators(Validators.required);
      this.editForm.get('parentDonationId')?.setValidators(Validators.required);
    } else {
      this.editForm.patchValue({
        parentDonorName: '',
        parentDonorId: null,
        parentDonationId: null,
        pacLimitNotAffected: false
      });

      this.editForm.get('parentDonorName')?.clearValidators();
      this.editForm.get('parentDonationId')?.clearValidators();
    }

    this.editForm.get('parentDonorName')?.updateValueAndValidity();
    this.editForm.get('parentDonationId')?.updateValueAndValidity();
  }

  filterParentDonationNames(event: any): void {
    const query = event.query;
    this.service.searchParentDonors(query, this.campaignId).subscribe({
      next: (data: DropdownOption[]) => { 
        this.parentDonorSearchResults = data.map(option => ({
          text: option.text,
          value: option.value,
          checkNumber: '' 
        }));
        this.filteredParentDonationNames = data.map(option => option.text);
      },
      error: () => {
        this.parentDonorSearchResults = [];
        this.filteredParentDonationNames = [];
      }
    });
  }

  private fetchParentDonationNumbers(parentDonorId: number): void {
    this.service.getParentDonations(parentDonorId).subscribe({
      next: (data: ParentDonationSearchModel[]) => {
        this.parentDonationNumberOptions = data.map(donation => ({
          label: `Parent Donation Number ${donation.value}`,
          value: Number(donation.value)
        }));
        this.editForm.patchValue({
          parentDonationId: this.parentInfo?.parentDonationId || null
        });
      },
      error: () => {
        this.parentDonationNumberOptions = [];
        console.error('Failed to fetch parent donations.');
      }
    });
  }

  onParentDonationNameSelect(event: AutoCompleteSelectEvent): void {
    const selectedDonorName = event.value; 
    const selectedDonor = this.parentDonorSearchResults.find(donor => donor.text === selectedDonorName); 

    if (selectedDonor) {
      this.editForm.patchValue({ parentDonorId: selectedDonor.value });
      this.fetchParentDonationNumbers(Number(selectedDonor.value));
    } else {
      console.error('Selected donor not found in search results.');
    }
  }
}
